import React, { useState, useEffect, useRef } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Search as SearchIcon, Loader2, Sparkles, Lightbulb, Users, BookOpen, ArrowRight, ShieldCheck, Download, FileText, Share2, Copy, ListChecks, BarChart3, Grid2x2, MessageCircle, ImageIcon, Building2, Globe, Coins } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError, API } from "@/lib/api";
import { CreditPill } from "@/components/CreditPill";
import { ShareButton } from "@/components/ShareButton";
import { toast } from "sonner";

const RUDRAM_SITE = "https://rudram.ai";

function Photo({ img, className }) {
  const [ok, setOk] = React.useState(true);
  if (!img?.url || !ok) return null;
  return (
    <a href={img.source_url || img.url} target="_blank" rel="noopener noreferrer" className={`block group relative overflow-hidden rounded-xl border border-rud bg-elevated ${className || ""}`} title={img.title}>
      <img src={img.url} alt={img.title || ""} loading="lazy" onError={() => setOk(false)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      {(img.creator || img.license) && (
        <span className="absolute bottom-0 inset-x-0 text-[9px] text-white/90 bg-black/50 px-2 py-0.5 truncate">{[img.creator, img.license].filter(Boolean).join(" · ")}</span>
      )}
    </a>
  );
}

function Logo({ media }) {
  const [src, setSrc] = React.useState(media.logo || media.favicon || "");
  React.useEffect(() => { setSrc(media.logo || media.favicon || ""); }, [media.logo, media.favicon]);
  if (!src) return <div className="w-14 h-14 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Building2 size={22} /></div>;
  return <img src={src} alt="logo" onError={() => setSrc(media.favicon && src !== media.favicon ? media.favicon : "")} className="w-14 h-14 rounded-xl object-contain bg-white p-1.5 border border-rud shrink-0" />;
}

function LeaderAvatar({ leader }) {
  const [ok, setOk] = React.useState(true);
  if (leader.photo && ok) {
    return <img src={leader.photo} alt={leader.name} className="w-full h-full object-cover" onError={() => setOk(false)} />;
  }
  return <Users size={20} />;
}

function MediaBlock({ media }) {
  if (!media) return null;
  const isCompany = media.type === "company";
  // Irrelevant stock imagery removed per product direction — the single relevant
  // topic vector is offered via the Share panel. Only factual company info remains.
  if (!isCompany) return null;
  return (
    <div className="rud-card rounded-2xl p-6" data-testid="search-media">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4">
        <Building2 size={14} /> Company
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Logo media={media} />
        <div className="font-serif text-2xl leading-tight" data-testid="media-company-name">{media.name}</div>
      </div>

      {media.leaders?.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-sec mb-3">Leadership</div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="media-leaders">
            {media.leaders.map((l, i) => (
              <div key={i} className="flex gap-3 rounded-xl border border-rud bg-elevated p-3" data-testid={`media-leader-${i}`}>
                <div className="w-14 h-14 rounded-full overflow-hidden bg-brand/15 grid place-items-center text-brand shrink-0">
                  <LeaderAvatar leader={l} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{l.name}</div>
                  {l.role && <div className="text-[11px] text-brand">{l.role}</div>}
                  {l.bio && <div className="text-xs text-sec mt-0.5 line-clamp-3">{l.bio}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="text-[10px] text-sec mt-3">Names/bios AI-compiled — verify before use.</div>
    </div>
  );
}

function ThemedChart({ chart }) {
  const series = (chart?.series || []).filter((s) => typeof s.value === "number");
  if (series.length === 0) return null;
  const max = Math.max(...series.map((s) => s.value), 1);
  const isLine = chart.type === "line";
  return (
    <div className="rud-card rounded-2xl p-6" data-testid="search-chart">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><BarChart3 size={14} /> {chart.title || "Data"}</div>
      {chart.y_label && <div className="text-[11px] text-sec mb-4">{chart.y_label}</div>}
      {isLine ? <LineChart series={series} max={max} /> : (
        <div className="space-y-2.5 mt-3" data-testid="search-chart-bars">
          {series.map((s, i) => {
            const shade = 0.35 + (0.6 * (i / Math.max(1, series.length - 1)));
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="w-28 shrink-0 text-xs text-sec truncate text-right">{s.label}</div>
                <div className="flex-1 h-6 rounded-md bg-elevated overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(s.value / max) * 100}%` }} transition={{ duration: 0.6, delay: i * 0.05 }}
                    className="h-full rounded-md" style={{ background: `color-mix(in srgb, var(--rud-brand) ${Math.round(shade * 100)}%, transparent)` }} />
                </div>
                <div className="w-20 shrink-0 text-xs font-medium tabular-nums">{s.value.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      )}
      {chart.source_note && <div className="text-[11px] text-sec mt-4 italic">{chart.source_note}</div>}
    </div>
  );
}

function LineChart({ series, max }) {
  const W = 560, H = 180, pad = 8;
  const step = series.length > 1 ? (W - pad * 2) / (series.length - 1) : 0;
  const pts = series.map((s, i) => [pad + i * step, H - pad - (s.value / max) * (H - pad * 2)]);
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44 mt-3" data-testid="search-chart-line" preserveAspectRatio="none">
      <path d={d} fill="none" stroke="var(--rud-brand)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill="var(--rud-brand)" />)}
    </svg>
  );
}

function SwotGrid({ swot }) {
  const cells = [
    { key: "strengths", label: "Strengths" },
    { key: "weaknesses", label: "Weaknesses" },
    { key: "opportunities", label: "Opportunities" },
    { key: "threats", label: "Threats" },
  ];
  return (
    <div className="rud-card rounded-2xl p-6" data-testid="search-swot">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><Grid2x2 size={14} /> SWOT Analysis</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {cells.map((c) => (
          <div key={c.key} className="rounded-xl border border-rud bg-elevated p-4" data-testid={`swot-${c.key}`}>
            <div className="text-sm font-semibold mb-2">{c.label}</div>
            <ul className="space-y-1.5">{(swot[c.key] || []).map((x, i) => <li key={i} className="text-xs text-sec flex gap-2"><span className="text-brand mt-0.5">·</span>{x}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Search() {
  const { lang, user, setUser } = useApp();
  const brand = user?.branding || {};
  const searchLocation = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  const peopleTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [lastQuery, setLastQuery] = useState("");
  const [exporting, setExporting] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [vecUrl, setVecUrl] = useState("");
  const [vecLoading, setVecLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [saveScope, setSaveScope] = useState("personal");
  const [library, setLibrary] = useState([]);
  const [opened, setOpened] = useState(null); // {owner_name, charged, creator_earned, is_owner}
  const [nudge, setNudge] = useState(null); // {streak} shown after a free search

  const loadLibrary = async () => {
    try { const { data } = await api.get("/reports", { params: { limit: 12 } }); setLibrary(data.reports || []); } catch (_) {}
  };
  useEffect(() => { loadLibrary(); }, []);

  const run = async (query) => {
    const term = (query ?? q).trim();
    if (!term) { toast.error("Type something to search."); return; }
    if (user === undefined) return; // auth still resolving — the ?q= effect will retry
    if (user === null) {
      try { sessionStorage.setItem("rud_post_login_redirect", `/search?q=${encodeURIComponent(term)}`); } catch (_) {}
      toast.error("Sign in to search."); window.location.assign("/auth"); return;
    }
    setQ(term); setLoading(true); setRes(null); setShareOpen(false); setVecUrl(""); setSavedId(""); setOpened(null); setPushed(false); setNudge(null);
    try {
      const { data } = await api.post("/search", { query: term, lang });
      setRes(data.result);
      setLastQuery(term);
      if (typeof data.credits_remaining === "number" && setUser) setUser((u) => (u ? { ...u, credits: data.credits_remaining, streak_days: data.streak_days ?? u.streak_days, free_search_today: false } : u));
      if (data.was_free) {
        toast.success(data.daily_free ? `Free search of the day ✨ ${data.streak_days > 1 ? `· 🔥 ${data.streak_days}-day streak` : ""}` : "Your free search ✨");
        setNudge({ streak: data.streak_days || 0 });
      } else if (data.charged > 0) { toast.success(`Search · −${data.charged} credits`); setNudge(null); }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { if (e.response?.status !== 402) toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const saveReport = async () => {
    if (!res) return;
    if (!user) { toast.error("Sign in to save reports and earn."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/reports/save", { query: lastQuery, result: res, lang, scope: saveScope });
      setSavedId(data.id);
      if (typeof data.credits_remaining === "number" && setUser) setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      const chargeNote = data.was_free ? "your 1 free save ✨" : (data.charged > 0 ? `−${data.charged} credits` : "");
      toast.success(`Saved as ${saveScope}${chargeNote ? ` (${chargeNote})` : ""} — opens for ${saveScope === "professional" ? "0.5" : "0.25"} credits, you earn 15% each time.`);
      loadLibrary();
    } catch (e) { if (e.response?.status !== 402) toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pushToGalactopedia = async () => {
    if (!res) return;
    if (!user) { toast.error("Sign in to contribute to Galactopedia."); return; }
    const content = [res.executive_summary, res.answer,
      (res.key_points || []).join(" ")].filter(Boolean).join("\n\n").slice(0, 9000);
    if (content.trim().length < 40) { toast.error("Not enough content to contribute."); return; }
    setPushing(true);
    try {
      const { data } = await api.post("/galactopedia/push", {
        title: res.title || lastQuery, content, source_type: "report",
      });
      if (typeof data.credits_remaining === "number" && setUser) {
        setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      }
      if (data.accepted) {
        setPushed(true);
        toast.success(`Accepted into Galactopedia — you earned ${data.reward} credit! 🌌`);
      } else {
        toast.error(`Gatekeeper: ${data.reason}${data.refunded ? ` (your ${data.refunded} credit hold was refunded)` : ""}.`);
      }
    } catch (e) { if (e.response?.status !== 402) toast.error(apiError(e.response?.data?.detail)); }
    finally { setPushing(false); }
  };

  const openReport = async (id) => {    if (!user) { toast.error("Sign in to open saved reports."); return; }
    setLoading(true); setRes(null); setShareOpen(false); setVecUrl(""); setSavedId("");
    try {
      const { data } = await api.get(`/reports/${id}`);
      setRes(data.result);
      setLastQuery(data.query || "");
      setOpened({ owner_name: data.owner_name, charged: data.charged, creator_earned: data.creator_earned, is_owner: data.is_owner, already: data.already_unlocked });
      if (data.charged && typeof data.credits_remaining === "number" && setUser) {
        setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // Live people search — find registered members by name as you type (free, no credits).
  useEffect(() => {
    clearTimeout(peopleTimer.current);
    const term = q.trim();
    if (!user || term.length < 2) { setPeople([]); return; }
    peopleTimer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(term)}`); setPeople(data.members || []); }
      catch { setPeople([]); }
    }, 300);
    return () => clearTimeout(peopleTimer.current);
  }, [q, user]);

  const autoRunRef = useRef("");
  useEffect(() => {
    const params = new URLSearchParams(searchLocation.search);
    const rid = params.get("report");
    if (rid) { openReport(rid); return; }
    const qp = params.get("q");
    if (!qp) return;
    if (user === undefined) return;            // wait until auth resolves (re-runs when `user` changes)
    if (autoRunRef.current === qp) return;     // dedupe StrictMode/re-render double-fire
    autoRunRef.current = qp;
    run(qp);
    // eslint-disable-next-line
  }, [searchLocation.search, user]);

  const exportAs = async (fmt) => {
    if (!res) return;
    setExporting(fmt);
    try {
      const { data } = await api.post("/export", {
        title: res.title || lastQuery || "My Kind Search",
        deliverable_label: "My Kind Search",
        content: res,
        format: fmt,
        site_url: RUDRAM_SITE,
        brand_company: brand.company_name || "",
        brand_logo: brand.logo || "",
        brand_name: brand.display_name || user?.name || "",
        brand_title: brand.title || "",
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `My Kind-Search.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed. Please try again."); }
    finally { setExporting(""); }
  };

  const shareUrl = vecUrl || `${window.location.origin}/search?q=${encodeURIComponent(lastQuery)}`;
  const shareText = `${lastQuery} — answered on My Kind (ad-free search)`;
  const shareTargets = [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}` },
    { id: "twitter", label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
    { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { id: "telegram", label: "Telegram", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
  ];
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — paste in WeChat, email, anywhere."); } catch { toast.error("Couldn't copy link."); } };

  // Lazily generate a topic-specific monochrome+heart-red share vector the first time the panel opens.
  const openShare = async () => {
    const next = !shareOpen;
    setShareOpen(next);
    if (next && !vecUrl && !vecLoading && lastQuery) {
      setVecLoading(true);
      try {
        const { data } = await api.post("/search/share", {
          query: lastQuery,
          kind: "search",
          summary: res?.executive_summary || res?.answer?.slice(0, 240) || "",
          href: `/search?q=${encodeURIComponent(lastQuery)}`,
        });
        if (data?.url) setVecUrl(data.url);
      } catch { /* keep the plain /search fallback */ }
      finally { setVecLoading(false); }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><SearchIcon size={14} /> My Kind Search</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Answers, not ads.</h1>
        <p className="text-sec mt-3"><b className="text-[var(--rud-text)]">Ad-free, clutter-free, non-promotional.</b> Ask anything under the sky — a research report, sentiment, signal, competitive intelligence, SWOT, financials, a comparison, a how-to, a what-if or what-is. My Kind shapes the answer to your <b className="text-[var(--rud-text)]">exact</b> ask.</p>
        <p className="text-sec text-sm mt-2">Save your reports to My Kind — when anyone else opens one, you <b className="text-brand">earn 15%</b> of their credits, every time.</p>
        <div className="mt-4 flex justify-center">
          <ShareButton slug="search" title="Honest Search · My Kind" text="Ad-free, AI-native search on My Kind." label="Share My Kind" className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2" />
        </div>
      </div>

      <div className="mt-8 flex gap-2 max-w-2xl mx-auto">
        <div className="relative flex-1">
          <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="Ask anything - clutter free, ad-free"
            className="rud-input w-full rounded-full pl-10 pr-4 py-3 text-sm"
          />
        </div>
        <button data-testid="search-run" onClick={() => run()} disabled={loading} className="btn-primary px-6 rounded-full flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <SearchIcon size={16} />} Search
          {user && <CreditPill cost={Number(user.search_cost ?? 0.03)} free={!!user.free_search_today} />}
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 mt-3 text-[11px] text-sec">
        <ShieldCheck size={13} className="text-brand" /> No ads · No tracking manipulation · No discrimination
      </div>

      {user && (
        <div className="flex items-center justify-center gap-2 mt-2 text-[11px] text-sec" data-testid="search-cost-hint">
          {user.free_search_today
            ? <span>✨ Your <b className="text-brand">free search of the day</b> is ready{user.streak_days > 0 ? <> · 🔥 <b className="text-brand">{user.streak_days}-day streak</b></> : null} — then {Number(user.search_cost ?? 0.03).toFixed(2)} credits each, fair-priced for your region.</span>
            : <span>{user.streak_days > 1 ? <>🔥 <b className="text-brand">{user.streak_days}-day streak</b> · </> : null}<b className="text-[var(--rud-text)]">{Number(user.search_cost ?? 0.03).toFixed(2)}</b> credits / search (fair-priced for your region) · balance <b className="text-brand">{Number(user.credits || 0).toFixed(2)}</b> · come back tomorrow for a free one</span>}
        </div>
      )}

      {people.length > 0 && (
        <div className="mt-6 max-w-2xl mx-auto rud-card rounded-2xl p-4" data-testid="search-people">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Users size={14} /> People on My Kind</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {people.map((m) => (
              <button key={m.user_id} data-testid={`search-person-${m.user_id}`} onClick={() => navigate(`/u/${m.user_id}`)}
                className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 hover:bg-white/10 text-left transition-colors">
                <span className="w-9 h-9 rounded-full bg-brand/15 text-brand grid place-items-center text-sm font-bold overflow-hidden shrink-0">
                  {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" /> : (m.name?.[0] || "?").toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{m.name}</span>
                  <span className="block text-[11px] text-sec truncate">{m.city || m.headline || "Member"}</span>
                </span>
                <ArrowRight size={14} className="text-sec shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {!res && !loading && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6 max-w-2xl mx-auto" data-testid="search-examples">
          {[
            { label: "Competitive Intelligence", prompt: "Competitive intelligence on " },
            { label: "SWOT", prompt: "SWOT analysis of " },
            { label: "Financials", prompt: "Financials and unit economics of " },
            { label: "Comparison", prompt: "Compare " },
            { label: "How-to", prompt: "How to " },
          ].map((c) => (
            <button
              key={c.label}
              data-testid={`search-example-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { setQ(c.prompt); }}
              className="text-xs text-sec bg-elevated border border-rud rounded-full px-3.5 py-1.5 hover:border-brand hover:text-[var(--rud-text)] transition-colors"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {!res && !loading && library.length > 0 && (
        <div className="mt-12 max-w-3xl mx-auto" data-testid="reports-library">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><BookOpen size={14} /> Reports saved by the community</div>
          <p className="text-xs text-sec mb-4">Skip the wait — open a ready-made report. Opening costs 5 credits, and the creator earns 15%. Save your own to start earning.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {library.map((r) => (
              <button key={r.id} data-testid={`report-${r.id}`} onClick={() => openReport(r.id)}
                className="text-left rud-card rounded-2xl p-4 hover:border-brand/50 transition-colors group">
                <div className="font-medium text-sm group-hover:text-brand transition-colors line-clamp-1">{r.title || r.query}</div>
                {r.snippet && <p className="text-xs text-sec mt-1 line-clamp-2">{r.snippet}</p>}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-rud text-[11px] text-sec">
                  <span>by {r.owner_name}</span>
                  <span className="ml-auto flex items-center gap-1"><Users size={11} /> {r.uses} uses</span>
                  <span className="flex items-center gap-1 text-brand"><ArrowRight size={11} /> Open · 5 cr</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-10 text-center text-sec"><HeartLoader /><p className="text-sm">My Kind is thinking clearly…</p></div>
      )}

      {res && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-5" data-testid="search-result">
          {nudge && user && !user.auto_topup_enabled && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              data-testid="first-search-nudge"
              className="rud-card rounded-2xl p-4 sm:p-5 border border-brand/40 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
            >
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-brand mt-0.5 shrink-0" />
                <div className="text-sm">
                  <b className="text-[var(--rud-text)]">That one was on us{nudge.streak > 1 ? ` · 🔥 ${nudge.streak}-day streak` : ""}.</b>
                  <span className="text-sec"> Grab 5 credits to keep exploring — every next answer is region-fair from <b className="text-brand">{Number(user.search_cost ?? 0.03).toFixed(2)}</b> credits.</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="nudge-add-credits" onClick={() => window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message: "Keep exploring — add credits" } }))} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Coins size={14} /> Add 5 credits</button>
                <button data-testid="nudge-dismiss" onClick={() => setNudge(null)} className="text-sec hover:text-[var(--rud-text)] text-sm px-2">Later</button>
              </div>
            </motion.div>
          )}
          {/* Export & Share bar */}
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <button data-testid="search-export-pdf" onClick={() => exportAs("pdf")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
              {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
            </button>
            <button data-testid="search-export-docx" onClick={() => exportAs("docx")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
              {exporting === "docx" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Word
            </button>
            <button data-testid="search-export-pptx" onClick={() => exportAs("pptx")} disabled={!!exporting} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
              {exporting === "pptx" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PPT
            </button>
            {!savedId && (
              <div className="flex items-center rounded-full border border-rud overflow-hidden text-xs" data-testid="save-scope-toggle">
                {["personal", "professional"].map((s) => (
                  <button key={s} data-testid={`save-scope-${s}`} onClick={() => setSaveScope(s)}
                    className={`h-9 px-3 capitalize transition-colors ${saveScope === s ? "bg-brand text-black font-semibold" : "text-sec hover:text-brand"}`}>
                    {s} · {s === "professional" ? "0.5" : "0.25"}cr
                  </button>
                ))}
              </div>
            )}
            <button data-testid="search-save" onClick={saveReport} disabled={saving || !!savedId} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} {savedId ? "Saved ✓" : "Save & Earn"}
            </button>
            <button data-testid="search-push-galactopedia" onClick={pushToGalactopedia} disabled={pushing || pushed} title="Contribute this to the collective knowledge base — earn 0.1 credit if accepted"
              className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
              {pushing ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} {pushed ? "In Galactopedia ✓" : "Push to Galactopedia"}
            </button>
            <div className="relative">
              <button data-testid="search-share-btn" onClick={openShare} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Share2 size={14} /> Share</button>
              {shareOpen && (
                <div className="absolute right-0 mt-2 w-60 rounded-xl glass border border-rud p-2 z-30" data-testid="search-share-menu">
                  <div className="px-2 pt-1 pb-2 flex items-center gap-2 text-[11px] text-sec" data-testid="share-vec-status">
                    {vecLoading ? (<><HeartLoader size={16} /> <span>Painting a share vector for “{lastQuery}”…</span></>)
                      : vecUrl ? (<><ImageIcon size={13} className="text-brand" /> <span>Custom art ready — link previews with your topic vector.</span></>)
                      : (<span>Share this answer.</span>)}
                  </div>
                  {shareTargets.map((s) => (
                    <a key={s.id} data-testid={`share-${s.id}`} href={s.href} target="_blank" rel="noopener noreferrer" className="block rud-hl rounded-md px-3 py-2 text-sm">{s.label}</a>
                  ))}
                  <button data-testid="share-copy" onClick={copyLink} className="w-full text-left rud-hl rounded-md px-3 py-2 text-sm flex items-center gap-2"><Copy size={13} /> Copy link (WeChat, email…)</button>
                </div>
              )}
            </div>
          </div>

          {opened && (
            <div className="rounded-xl border border-brand/30 bg-brand/10 px-4 py-2.5 text-sm flex items-center gap-2 flex-wrap" data-testid="search-opened-banner">
              <BookOpen size={14} className="text-brand" />
              {opened.is_owner ? (
                <span>Your saved report — free to reopen anytime.</span>
              ) : opened.already ? (
                <span>You've already unlocked this report by <b>{opened.owner_name}</b> — free to reopen.</span>
              ) : (
                <span>Opened a saved report by <b>{opened.owner_name}</b> · <b>{opened.charged} credits</b> spent · creator earned <b className="text-brand">{opened.creator_earned}</b>.</span>
              )}
            </div>
          )}

          <div className="rud-card rounded-2xl p-6" data-testid="search-output-header">
            {(res.title || lastQuery) && <h2 className="font-serif text-2xl sm:text-3xl tracking-tight leading-snug" data-testid="search-title">{res.title || lastQuery}</h2>}
            {lastQuery && <p className="text-xs text-sec mt-2" data-testid="search-question"><span className="uppercase tracking-widest">You asked</span> · {lastQuery}</p>}
            {res.executive_summary && (
              <div className="mt-4 border-l-2 border-brand/60 pl-4" data-testid="search-exec-summary">
                <div className="text-[11px] uppercase tracking-widest text-brand mb-1">Executive summary</div>
                <p className="text-sm leading-relaxed">{res.executive_summary}</p>
              </div>
            )}
          </div>

          <div className="rud-card rounded-2xl p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Sparkles size={14} /> Answer</div>
            <div className="anxya-article text-sm leading-relaxed" data-testid="search-answer">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-brand underline hover:opacity-80" />,
                h1: ({ node, ...props }) => <h2 {...props} className="font-serif text-2xl tracking-tight mt-5 mb-2" />,
                h2: ({ node, ...props }) => <h2 {...props} className="font-serif text-xl tracking-tight mt-5 mb-2" />,
                h3: ({ node, ...props }) => <h3 {...props} className="font-medium text-base mt-4 mb-1.5" />,
                ul: ({ node, ...props }) => <ul {...props} className="list-disc ml-5 space-y-1 my-2" />,
                ol: ({ node, ...props }) => <ol {...props} className="list-decimal ml-5 space-y-1 my-2" />,
                p: ({ node, ...props }) => <p {...props} className="my-2" />,
                strong: ({ node, ...props }) => <strong {...props} className="text-[var(--rud-text)] font-semibold" />,
                blockquote: ({ node, ...props }) => <blockquote {...props} className="border-l-2 border-brand pl-3 italic text-sec my-3" />,
                code: ({ node, ...props }) => <code {...props} className="bg-elevated border border-rud rounded px-1 py-0.5 text-xs" />,
                table: ({ node, ...props }) => <div className="overflow-x-auto my-3"><table {...props} className="w-full text-xs border border-rud" /></div>,
                th: ({ node, ...props }) => <th {...props} className="border border-rud px-2 py-1 bg-elevated text-left" />,
                td: ({ node, ...props }) => <td {...props} className="border border-rud px-2 py-1" />,
              }}>{res.answer || ""}</ReactMarkdown>
            </div>
          </div>

          {res.media && <MediaBlock media={res.media} />}

          {res.swot && Object.values(res.swot).some((v) => Array.isArray(v) && v.length) && <SwotGrid swot={res.swot} />}
          {res.metrics?.length > 0 && (
            <div className="rud-card rounded-2xl p-6" data-testid="search-metrics">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-4"><BarChart3 size={14} /> Key metrics</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {res.metrics.map((m, i) => (
                  <div key={i} className="rounded-xl border border-rud bg-elevated p-4">
                    <div className="text-lg font-semibold tabular-nums">{m.value}</div>
                    <div className="text-[11px] text-sec mt-0.5">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(res.charts) && res.charts.filter((c) => c?.series?.length).map((c, i) => <ThemedChart key={i} chart={c} />)}
          {res.chart && res.chart.series?.length > 0 && !(res.charts?.length) && <ThemedChart chart={res.chart} />}

          {res.table && res.table.rows?.length > 0 && (
            <div className="rud-card rounded-2xl p-6" data-testid="search-table">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ListChecks size={14} /> {res.table.title || "List"}</div>
                <button data-testid="search-export-xlsx" onClick={() => exportAs("xlsx")} disabled={!!exporting} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5 disabled:opacity-60">
                  {exporting === "xlsx" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Excel
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  {res.table.columns?.length > 0 && (
                    <thead><tr className="border-b border-rud">{res.table.columns.map((c, i) => <th key={i} className="text-left py-2 pr-4 font-semibold text-xs uppercase tracking-wide text-sec">{c}</th>)}</tr></thead>
                  )}
                  <tbody>
                    {res.table.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-rud/60">{(Array.isArray(row) ? row : [row]).map((cell, ci) => <td key={ci} className="py-2 pr-4 align-top">{cell}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {res.slides?.length > 0 && (
            <div className="rud-card rounded-2xl p-6" data-testid="search-deck">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><FileText size={14} /> Presentation · {res.slides.length} slides</div>
                <button data-testid="search-export-deck" onClick={() => exportAs("pptx")} disabled={!!exporting} className="btn-primary rounded-full h-8 px-4 text-xs flex items-center gap-1.5 disabled:opacity-60">
                  {exporting === "pptx" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PPT
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {res.slides.map((sl, i) => (
                  <div key={i} className="rounded-xl border border-rud bg-elevated p-4" data-testid={`deck-slide-${i}`}>
                    <div className="text-[10px] text-sec mb-1">Slide {i + 1}</div>
                    <div className="text-sm font-semibold mb-2 leading-tight">{sl.title}</div>
                    <ul className="space-y-1">{(sl.bullets || []).map((b, bi) => <li key={bi} className="text-xs text-sec flex gap-1.5"><span className="text-brand mt-0.5">·</span>{b}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {res.steps?.length > 0 && (
            <div className="rud-card rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><ListChecks size={14} /> Steps</div>
              <ol className="space-y-1.5 list-decimal pl-5">{res.steps.map((k, i) => <li key={i} className="text-sm text-sec">{k}</li>)}</ol>
            </div>
          )}

          {res.key_points?.length > 0 && (
            <div className="rud-card rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Lightbulb size={14} /> Key points</div>
              <ul className="space-y-1.5">{res.key_points.map((k, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand mt-1">·</span>{k}</li>)}</ul>
            </div>
          )}

          {res.perspectives?.length > 0 && (
            <div className="rud-card rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Users size={14} /> Perspectives</div>
              <ul className="space-y-1.5">{res.perspectives.map((k, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand mt-1">·</span>{k}</li>)}</ul>
            </div>
          )}

          {res.sources?.length > 0 && (
            <div className="rud-card rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><BookOpen size={14} /> Reference types</div>
              <div className="space-y-2">{res.sources.map((s, i) => (
                <div key={i} className="rounded-lg bg-elevated border border-rud p-3"><div className="text-sm font-medium">{s.title}</div>{s.note && <div className="text-xs text-sec mt-0.5">{s.note}</div>}</div>
              ))}</div>
            </div>
          )}

          {res.followups?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-sec mb-2">Ask next</div>
              <div className="flex flex-wrap gap-2">
                {res.followups.map((f, i) => (
                  <button key={i} data-testid={`search-followup-${i}`} onClick={() => run(f)} className="text-xs text-sec bg-elevated border border-rud rounded-full px-3 py-1.5 hover:border-brand hover:text-[var(--rud-text)] transition-colors flex items-center gap-1.5">
                    {f} <ArrowRight size={12} />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="text-center pt-4 border-t border-rud mt-6">
            <span className="text-sm text-sec">Done on </span>
            <a href={RUDRAM_SITE} target="_blank" rel="noopener noreferrer" data-testid="rudram-footer" className="text-sm text-blue-500 underline hover:text-blue-400 transition-colors">My Kind</a>
            <span className="text-sm text-sec">.</span>
          </div>
        </motion.div>
      )}
    </div>
  );
}
